import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import decode from 'jwt-decode';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { loginUUID, rememberMeLogin } from 'src/app/app.constants';
import { environment } from '../../../../environments/environment';
import { Authentication } from '../models/authentication.model';
import { Credentials } from '../models/credentials';
import { CredentialsSocial } from '../models/credentialsSocial';
import { MatDialog } from '@angular/material/dialog';

const ACCESS_TOKEN = 'access_token';
const REFRESH_TOKEN = 'refresh_token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    public http: HttpClient, 
    public router: Router,
    private dialog: MatDialog
  ) {}

  handleStorage(): any {
    const rememberMe = localStorage.getItem(rememberMeLogin) === 'true';
    return rememberMe ? localStorage : sessionStorage;
  }

  login(credentials: Credentials): Observable<Authentication> {
    const body = new HttpParams()
      .set('grant_type', 'two_factor')
      .set('username', credentials.email)
      .set('password', credentials.password)
      .set('scope', 'read')
      .set('device', 'web')
      .set('trust_device', credentials.trust_device);
    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers
      })
      .pipe(
        map((response: Authentication) => {
          this.handleStorage().removeItem(ACCESS_TOKEN);
          this.handleStorage().removeItem(REFRESH_TOKEN);
          return response;
        })
      );
  }

  loginWithTwoFactorCode(
    email: string,
    password: string,
    twoFactorCode: string,
    trustDevice: boolean
  ): Observable<Authentication> {
    const body = new HttpParams()
      .set('grant_type', 'two_factor') // same grant; backend now expects only the code
      .set('username', email)
      .set('password', password)
      .set('code', twoFactorCode) // <-- server looks for this (based on error string)
      .set('scope', 'read')
      .set('device', 'web')
      .set('trust_device', String(trustDevice)); // keep device trust behavior

    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http
      .post(`${environment.api_url}/oauth/token`, body, { headers })
      .pipe(
        map((response: Authentication) => {
          this.handleStorage().removeItem(ACCESS_TOKEN);
          this.handleStorage().removeItem(REFRESH_TOKEN);
          return response;
        })
      );
  }

  socialLogin(
    credentialsSocial: CredentialsSocial
  ): Observable<Authentication> {
    const body = new HttpParams()
      .set('grant_type', credentialsSocial.provider.toLowerCase())
      .set('social_token', credentialsSocial.authToken)
      .set('scope', 'read')
    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers
      })
      .pipe(
        map((response: Authentication) => {
          this.handleStorage().removeItem(ACCESS_TOKEN);
          this.handleStorage().removeItem(REFRESH_TOKEN);
          return response;
        })
      );
  }

  refreshToken(): Observable<Authentication> {
    const token = this.getToken(REFRESH_TOKEN);
    const body = new HttpParams()
      .set('grant_type', REFRESH_TOKEN)
      .set('refresh_token', token)
      .set('scope', 'read')
    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers
      })
      .pipe(
        map((response: Authentication) => {
          return response;
        })
      );
  }

  logout(): void {
    this.dialog.closeAll();
    this.handleStorage().removeItem(ACCESS_TOKEN);
    this.handleStorage().removeItem(REFRESH_TOKEN);
    this.router.navigate(['/sign/in'], { replaceUrl: true });
  }

  setSession(accessToken: string, refreshToken: string): void {
    this.handleStorage().setItem(ACCESS_TOKEN, accessToken);
    this.handleStorage().setItem(REFRESH_TOKEN, refreshToken);
  }

  getToken(type: string = ACCESS_TOKEN): string {
    return this.handleStorage().getItem(type);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (token) {
      return !this.isTokenExpired();
    } else {
      return false;
    }
  }

  getTokenExpirationDate(token: string): Date {
    const decoded = decode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(type: string = ACCESS_TOKEN): boolean {
    const token = this.getToken(type);
    if (!token) {
      return true;
    }

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) {
      return true;
    }
    return !(date.valueOf() > new Date().valueOf());
  }

  isAuthenticatedOrRefreshTokenNotExpired(): boolean {
    if (this.isAuthenticated() || !this.isTokenExpired(REFRESH_TOKEN)) {
      return true;
    } else if (
      window.location.pathname !== '/sign/in' &&
      window.location.pathname !== '/sign/up' &&
      window.location.pathname !== '/sign/forgot-password' &&
      window.location.pathname !== '/sign/password-confirmation'
    ) {
      this.logout();
      return false;
    }
    return false;
  }

  getCurrentUserId(): string {
    const token: any = this.getToken();
    return decode(token).user_id;
  }
}
